<template>
	<div class="friend-request-panel">
		<div class="panel-header">
			<div class="title-block">
				<div class="panel-title">新的朋友</div>
				<div class="panel-subtitle">查看并处理好友申请</div>
			</div>
			<el-button class="refresh-btn" icon="el-icon-refresh" size="small" :loading="loading"
				@click="loadRequests">刷新</el-button>
		</div>

		<div class="request-toolbar">
			<div class="request-tab" :class="{ active: activeTab === 'received' }" @click="switchTab('received')">
				<span>收到的</span>
				<span v-if="unreadCount > 0" class="tab-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
			</div>
			<div class="request-tab" :class="{ active: activeTab === 'sent' }" @click="switchTab('sent')">
				<span>发出的</span>
			</div>
		</div>

		<div class="request-content">
			<div v-if="loading && requests.length === 0" class="panel-state">
				<i class="el-icon-loading state-icon"></i>
				<div class="state-title">正在加载好友申请</div>
			</div>
			<div v-else-if="requests.length === 0" class="panel-state">
				<div class="empty-icon">友</div>
				<div class="state-title">暂无好友申请</div>
				<div class="state-text">{{ emptyText }}</div>
			</div>

			<el-scrollbar v-else class="request-scrollbar">
				<div class="request-list">
					<div v-for="request in requests" :key="request.id" class="request-card">
						<div class="card-accent"></div>
						<head-image :id="request.userId" :size="54" :name="request.nickName"
							:url="request.headImage" radius="8px"></head-image>
						<div class="request-info">
							<div class="name-row">
								<span class="nick-name">{{ request.nickName || request.userName }}</span>
								<span class="request-time">{{ formatTime(request.createdTime) }}</span>
							</div>
							<div class="verify-message">{{ request.verifyMessage || '请求添加你为好友' }}</div>
							<div class="user-name">用户名：{{ request.userName }}</div>
						</div>
						<div v-if="activeTab === 'received' && request.status === 0" class="request-actions">
							<el-button type="primary" size="small" :loading="actionId === request.id"
								@click="acceptRequest(request)">接受</el-button>
							<el-button size="small" :disabled="actionId === request.id"
								@click="rejectRequest(request)">拒绝</el-button>
						</div>
						<div v-else class="request-status" :class="statusClass(request.status)">
							{{ statusLabel(request.status) }}
						</div>
					</div>
					<div class="list-footer">已显示全部申请</div>
				</div>
			</el-scrollbar>
		</div>
	</div>
</template>

<script>
import HeadImage from '../common/HeadImage.vue';

export default {
	name: "FriendRequest",
	components: { HeadImage },
	props: {
		active: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			activeTab: "received",
			receivedRequests: [],
			sentRequests: [],
			unreadCount: 0,
			loading: false,
			actionId: null,
			loadSeq: 0
		}
	},
	computed: {
		requests() {
			return this.activeTab === "received" ? this.receivedRequests : this.sentRequests;
		},
		emptyText() {
			return this.activeTab === "received" ? "收到的好友申请会显示在这里" : "发出的好友申请会显示在这里";
		}
	},
	watch: {
		active(value) {
			if (value) {
				this.activate();
			}
		}
	},
	methods: {
		activate() {
			this.loadUnreadCount();
			this.loadRequests();
		},
		switchTab(tab) {
			if (this.activeTab === tab) return;
			this.activeTab = tab;
			this.loadRequests();
		},
		loadUnreadCount() {
			return this.$http({
				url: "/friend/request/unread-count",
				method: "get"
			}).then((count) => {
				this.unreadCount = Number(count) || 0;
				this.$emit("unread-change", this.unreadCount);
			});
		},
		loadRequests() {
			const targetTab = this.activeTab;
			const currentSeq = ++this.loadSeq;
			this.loading = true;
			return this.$http({
				url: `/friend/request/${targetTab}`,
				method: "get"
			}).then((requests) => {
				if (currentSeq !== this.loadSeq) return;
				if (targetTab === "received") {
					this.receivedRequests = requests || [];
					this.markReceivedRead();
				} else {
					this.sentRequests = requests || [];
				}
			}).finally(() => {
				if (currentSeq === this.loadSeq) {
					this.loading = false;
				}
			});
		},
		markReceivedRead() {
			return this.$http({
				url: "/friend/request/read",
				method: "put"
			}).then(() => {
				this.receivedRequests.forEach(request => {
					if (request.status === 0) request.readStatus = 1;
				});
				this.unreadCount = 0;
				this.$emit("unread-change", 0);
				this.$eventBus.$emit("friendRequestRead");
			});
		},
		acceptRequest(request) {
			if (this.actionId !== null) return;
			this.actionId = request.id;
			this.$http({
				url: `/friend/request/${request.id}/accept`,
				method: "post"
			}).then(async () => {
				this.$set(request, "status", 1);
				await this.friendStore.loadFriend();
				this.$message.success("已接受好友申请");
				this.$eventBus.$emit("friendRequestRead");
				await this.loadRequests();
			}).finally(() => {
				this.actionId = null;
			});
		},
		rejectRequest(request) {
			if (this.actionId !== null) return;
			this.$confirm(`确认拒绝“${request.nickName || request.userName}”的好友申请吗？`, "拒绝好友申请", {
				confirmButtonText: "拒绝",
				cancelButtonText: "取消",
				type: "warning"
			}).then(() => {
				this.actionId = request.id;
				return this.$http({
					url: `/friend/request/${request.id}/reject`,
					method: "post"
				}).then(async () => {
					this.$set(request, "status", 2);
					this.$message.success("已拒绝好友申请");
					this.$eventBus.$emit("friendRequestRead");
					await this.loadRequests();
				}).finally(() => {
					this.actionId = null;
				});
			}).catch(() => {});
		},
		statusLabel(status) {
			if (status === 1) return "已接受";
			if (status === 2) return "已拒绝";
			if (status === 3) return "已撤销";
			return this.activeTab === "sent" ? "等待验证" : "待处理";
		},
		statusClass(status) {
			if (status === 1) return "status-accepted";
			if (status === 2 || status === 3) return "status-rejected";
			return "status-pending";
		},
		formatTime(time) {
			return time ? this.$date.toTimeText(time, true) : "";
		},
		onFriendRequestUpdated() {
			this.loadUnreadCount();
			if (this.active) {
				this.loadRequests();
			}
		}
	},
	mounted() {
		this.$eventBus.$on("friendRequestUpdated", this.onFriendRequestUpdated);
		this.loadUnreadCount();
		if (this.active) {
			this.activate();
		}
	},
	beforeDestroy() {
		this.$eventBus.$off("friendRequestUpdated", this.onFriendRequestUpdated);
	}
}
</script>

<style scoped lang="scss">
.friend-request-panel {
	flex: 1;
	min-width: 0;
	min-height: 0;
	display: flex;
	flex-direction: column;
	color: var(--im-tech-text);
	background:
		linear-gradient(rgba(43, 223, 255, 0.035) 1px, transparent 1px),
		linear-gradient(90deg, rgba(43, 223, 255, 0.035) 1px, transparent 1px),
		linear-gradient(180deg, rgba(7, 32, 62, 0.2), rgba(2, 10, 24, 0.28));
	background-size: 42px 42px, 42px 42px, 100% 100%;
}

.panel-header {
	height: 60px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 22px;
	border-bottom: 1px solid rgba(43, 223, 255, 0.24);
	background: linear-gradient(180deg, rgba(10, 42, 82, 0.42), rgba(5, 20, 42, 0.3));
	box-sizing: border-box;
}

.title-block { text-align: left; }
.panel-title { color: var(--im-tech-text); font-size: 18px; font-weight: 700; }
.panel-subtitle { margin-top: 2px; color: var(--im-tech-text-muted); font-size: 12px; }

.refresh-btn {
	color: var(--im-tech-cyan);
	background: rgba(5, 28, 56, 0.38);
	border-color: rgba(43, 223, 255, 0.34);
}

.request-toolbar {
	height: 58px;
	flex-shrink: 0;
	display: flex;
	align-items: flex-end;
	justify-content: center;
	gap: 10px;
	border-bottom: 1px solid rgba(43, 223, 255, 0.14);
}

.request-tab {
	min-width: 120px;
	height: 42px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 7px;
	color: var(--im-tech-text-muted);
	border-bottom: 2px solid transparent;
	cursor: pointer;
	transition: all 0.2s ease;
}

.request-tab:hover { color: var(--im-tech-text); background: rgba(43, 223, 255, 0.06); }
.request-tab.active { color: var(--im-tech-cyan); border-bottom-color: var(--im-tech-cyan); text-shadow: 0 0 12px rgba(43, 223, 255, 0.32); }
.tab-badge { min-width: 18px; height: 18px; padding: 0 5px; display: inline-flex; align-items: center; justify-content: center; color: #fff; background: #ff5361; border-radius: 9px; box-sizing: border-box; font-size: 10px; font-weight: 700; }

.request-content { flex: 1; min-height: 0; display: flex; }
.request-scrollbar { flex: 1; min-width: 0; }
.request-scrollbar::v-deep .el-scrollbar__wrap { overflow-x: hidden; }
.request-list { max-width: 820px; margin: 0 auto; padding: 24px 30px; box-sizing: border-box; }

.request-card {
	position: relative;
	min-height: 96px;
	display: flex;
	align-items: center;
	margin-bottom: 14px;
	padding: 18px 20px 18px 24px;
	background: linear-gradient(135deg, rgba(7, 36, 70, 0.54), rgba(3, 18, 38, 0.6));
	border: 1px solid rgba(43, 223, 255, 0.26);
	border-radius: 8px;
	box-shadow: inset 0 0 22px rgba(43, 223, 255, 0.05), 0 8px 24px rgba(0, 7, 22, 0.2);
	overflow: hidden;
}

.request-card:hover { border-color: rgba(43, 223, 255, 0.5); box-shadow: inset 0 0 24px rgba(43, 223, 255, 0.08), 0 0 18px rgba(43, 223, 255, 0.1); }
.card-accent { position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: linear-gradient(180deg, #2bdfff, #087dff); box-shadow: 0 0 10px rgba(43, 223, 255, 0.6); }
.request-info { flex: 1; min-width: 0; margin-left: 16px; text-align: left; }
.name-row { display: flex; align-items: center; }
.nick-name { color: var(--im-tech-text); font-size: 16px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.request-time { margin-left: 12px; color: rgba(151, 190, 216, 0.55); font-size: 11px; white-space: nowrap; }
.verify-message { margin-top: 7px; color: rgba(220, 239, 250, 0.84); font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.user-name { margin-top: 5px; color: var(--im-tech-text-muted); font-size: 11px; }
.request-actions { flex-shrink: 0; display: flex; margin-left: 18px; }

.request-status {
	flex-shrink: 0;
	min-width: 68px;
	margin-left: 18px;
	padding: 6px 10px;
	border-radius: 5px;
	font-size: 12px;
	font-weight: 600;
	box-sizing: border-box;
}

.status-pending { color: #ffc76a; background: rgba(255, 177, 52, 0.12); border: 1px solid rgba(255, 195, 102, 0.28); }
.status-accepted { color: #48eadf; background: rgba(0, 214, 194, 0.11); border: 1px solid rgba(72, 234, 223, 0.26); }
.status-rejected { color: rgba(190, 218, 234, 0.62); background: rgba(127, 154, 171, 0.1); border: 1px solid rgba(164, 203, 225, 0.18); }
.list-footer { padding: 14px 0 24px; color: rgba(151, 190, 216, 0.48); font-size: 12px; text-align: center; }

.panel-state { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; color: var(--im-tech-text-muted); }
.state-icon { color: var(--im-tech-cyan); font-size: 38px; text-shadow: 0 0 14px rgba(43, 223, 255, 0.45); }
.empty-icon { width: 68px; height: 68px; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; color: var(--im-tech-cyan); font-size: 28px; font-weight: 800; background: rgba(0, 178, 220, 0.12); border: 1px solid rgba(43, 223, 255, 0.32); border-radius: 50%; box-shadow: 0 0 24px rgba(43, 223, 255, 0.1); }
.state-title { margin-top: 12px; color: var(--im-tech-text); font-size: 17px; font-weight: 600; }
.state-text { margin-top: 7px; color: var(--im-tech-text-muted); font-size: 13px; }
</style>
